import { useSystemInfo, useSystemViewData } from "../providers/helpers/useSystem"
import { Skeleton } from "@chakra-ui/react"
import ReportWithPeriodControls from "@hydrologiq/shared/reporting/ReportWithPeriodControls"
import useEntityDataRange from "@hydrologiq/shared/reporting/useEntityDataRange"
import type { ReportScopeString } from "@hydrologiq/shared/reporting/useReport"

/**
 * The unified entity performance report for a system / installation (KPIs +
 * graphs + wide table), served on the `reporting` route. The legacy CSV/download
 * reporting lives separately on the `download` route (SystemReporting).
 *
 * A non-grouped system with exactly one installation reports at "installation"
 * scope (matching the legacy single-installation path); every other system
 * reports at "system" scope.
 *
 * The cadence / period date selection and the PDF download are provided by the
 * shared {@link ReportWithPeriodControls}, exactly as the assets section does
 * (see AssetReport). The period selectors are bounded to the system's active
 * life via {@link useEntityDataRange}, which fetches the `{ earliest, latest }`
 * bounds from the backend `getEntityDataRange` resolver:
 *  - anchorEnd = `range.latest` (when the system was last active). Undefined
 *    while any installation is still active, so the wrapper anchors to now (the
 *    latest period).
 *  - lowerBound = `range.earliest`, so the period dropdowns never offer a window
 *    from before the system existed.
 * Raw ISO strings are passed through; the wrapper snaps them to
 * day/week/month edges when building the options (see periodOptions).
 */
const SystemReport = () => {
  const info = useSystemInfo()
  const systemViewData = useSystemViewData()

  const systemId = systemViewData?.system?.id
  const systemName = systemViewData?.system?.name
  const installations = systemViewData?.installations ?? []

  // Resolve scope + entity: a non-grouped system with exactly one installation
  // reports at installation scope; otherwise at system scope.
  const singleInstallation =
    !info?.grouped && installations.length === 1 ? installations[0].installation : undefined
  const scope: ReportScopeString = singleInstallation ? "installation" : "system"
  const entityId = singleInstallation?.id ?? systemId

  // The available window is owned by the backend resolver and consumed via the
  // shared hook (skips while entityId is unresolved).
  const { range } = useEntityDataRange(scope, entityId ?? "")

  if (!entityId) {
    return <Skeleton height="100%" width="100%" />
  }

  return (
    <ReportWithPeriodControls
      scope={scope}
      entityId={entityId}
      entityName={singleInstallation?.name ?? systemName}
      anchorEnd={range.latest}
      lowerBound={range.earliest}
    />
  )
}

export default SystemReport
